"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth/context";
import { useChores } from "@/lib/hooks/useChores";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useTransactions } from "@/lib/hooks/useTransactions";
import { useGoals } from "@/lib/hooks/useGoals";
import { useChoreProposals } from "@/lib/hooks/useChoreProposals";
import { useMembers } from "@/lib/hooks/useMembers";
import { useStreakStatus } from "@/lib/hooks/useStreakStatus";
import { markMomentSeen } from "@/lib/db/userPrefs";
import {
  ACHIEVEMENTS,
  computeUnlockedAchievements,
  levelFromXp,
  totalXp,
} from "@/lib/utils/achievements";
import { calculateStreak } from "@/lib/utils/streaks";
import { genderFromRelation } from "@/lib/utils/roles";
import type { MascotContext } from "./contexts";
import type { MomentData } from "./copy";

const STREAK_MILESTONES = [3, 7, 14, 30, 50, 100, 365];

/**
 * Czy data wpada w "dzisiaj" lokalnej strefie. Używane do welcome (pierwszy
 * dzień konta) + comeback (gap > 5 dni od ostatniej aktywności).
 */
const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export type ActiveMoment = {
  context: MascotContext;
  data: MomentData;
  /** Klucz do markMomentSeen — np. "level-up:5" lub "badge:streak-7". */
  seenKey: string;
  female: boolean;
};

/**
 * Detekcja persistent mascot moments (level-up, badge-unlocked) na bazie
 * realtime danych z Firestore. Tylko dla dziecka.
 *
 * Dla każdego trigera:
 * 1. Liczy aktualny stan (level / unlocked badges).
 * 2. Sprawdza czy `prefs.seenMoments[key]` zawiera już ten progres.
 * 3. Jeśli nie — otwiera moment (queue first-in-first-out).
 * 4. `dismiss()` zapisuje seenKey i wyciąga następny z kolejki.
 *
 * NIE obsługuje krótkotrwałych trigerów (task-submitted) — te są wpinane
 * inline w samych komponentach (CompletionSubmitSheet etc.).
 */
export function useMascotMomentTrigger(): {
  active: ActiveMoment | null;
  dismiss: () => void;
} {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const isChild = userDoc?.role === "child";

  const { chores } = useChores(isChild ? hid : undefined);
  // Members ładujemy zawsze (potrzebne też dla parent-philosophy — gender
  // dorosłego z relation).
  const { members } = useMembers(hid);
  const { completions: ownApproved } = useCompletions(
    isChild ? hid : undefined,
    { status: "approved", completedBy: uid },
  );
  const { transactions } = useTransactions(isChild ? hid : undefined, uid);
  const { goals } = useGoals(isChild ? hid : undefined, uid);
  const { proposals } = useChoreProposals(isChild ? hid : undefined, {
    proposedBy: uid,
  });
  const streakStatus = useStreakStatus(
    isChild ? hid : undefined,
    isChild ? uid : undefined,
  );

  const me = members.find((m) => m.userId === uid);
  const female = genderFromRelation(me?.relation) === "f";
  const childName = me?.displayName.split(" ")[0];
  const shieldUseCount = me?.streakShieldRescues?.length ?? 0;

  const seenMoments = userDoc?.prefs?.seenMoments ?? {};
  const isParent = userDoc?.role === "parent";

  const [queue, setQueue] = useState<ActiveMoment[]>([]);
  // Lokalny "tombstone" dismissed-keys — chroni przed race'em między
  // dismiss() (queue→[]) a server-write markMomentSeen (asynchronicznie).
  // Bez tego: użytkownik klika "Rozumiem" → queue puste → effect re-fires
  // (seenMoments to nowa referencja `?? {}` co render) → seenMoments[key]
  // jeszcze undefined (server nie zdążył) → moment dodaje się ponownie.
  const dismissedRef = useRef<Set<string>>(new Set());

  // Parent-only: manifest marki przy pierwszym wejściu (raz w życiu konta).
  // Gender wybiera maskotkę Mama/Tata.
  const parentGender = isParent ? genderFromRelation(me?.relation) : null;
  const isAdmin = me?.isAdmin === true;
  useEffect(() => {
    if (!isParent || !uid) return;
    // Założyciel bez ustawionej relation — RoleSetupDialog z AppShell
    // ma się pokazać PRZED manifestem. Blokujemy queueing parent-philosophy
    // dopóki user nie wybierze Mama/Tata; po wyborze me.relation wraca przez
    // listener members → effect re-runs → manifest queueuje się normalnie.
    if (isAdmin && !me?.relation) return;
    const seenKey = "parent-philosophy:2";
    if (seenMoments[seenKey]) return;
    if (dismissedRef.current.has(seenKey)) return;
    setQueue((prev) => {
      if (prev.some((m) => m.seenKey === seenKey)) return prev;
      return [
        ...prev,
        {
          context: "parent-philosophy",
          data: { parentGender },
          seenKey,
          female: parentGender === "f",
        },
      ];
    });
  }, [isParent, uid, seenMoments, parentGender, isAdmin, me?.relation]);

  useEffect(() => {
    if (!isChild || !uid) return;

    const candidates: ActiveMoment[] = [];

    // ── Level-up ──────────────────────────────────────────────
    const xp = totalXp(ownApproved);
    const level = levelFromXp(xp);
    if (level >= 2) {
      const seenKey = `level-up:${level}`;
      if (!seenMoments[seenKey]) {
        candidates.push({
          context: "level-up",
          data: { level, childName },
          seenKey,
          female,
        });
      }
    }

    // ── Badge-unlocked (diff) ─────────────────────────────────
    const unlocked = computeUnlockedAchievements({
      approved: ownApproved,
      transactions,
      goals,
      chores,
      proposals,
      shieldUseCount,
      userCreatedAt: firebaseUser?.metadata.creationTime
        ? new Date(firebaseUser.metadata.creationTime)
        : undefined,
    });

    for (const id of unlocked) {
      const seenKey = `badge:${id}`;
      if (seenMoments[seenKey]) continue;
      // welcome obsługujemy oddzielnie jako kontekst "welcome"
      if (id === "welcome") continue;
      const ach = ACHIEVEMENTS.find((a) => a.id === id);
      if (!ach) continue;
      candidates.push({
        context: "badge-unlocked",
        data: { badgeTitle: ach.title, childName },
        seenKey,
        female,
      });
    }

    // ── Streak milestone (3/7/14/30/50/100/365 dni) ───────────
    const streakDays = calculateStreak(ownApproved);
    for (const m of STREAK_MILESTONES) {
      if (streakDays < m) continue;
      const seenKey = `streak:${m}`;
      if (seenMoments[seenKey]) continue;
      candidates.push({
        context: "streak-milestone",
        data: { streakDays: m, childName },
        seenKey,
        female,
      });
    }

    // ── Goal-reached (per goal) ───────────────────────────────
    for (const g of goals) {
      if (!g.completedAt) continue;
      const seenKey = `goal:${g.id}`;
      if (seenMoments[seenKey]) continue;
      candidates.push({
        context: "goal-reached",
        data: { goalTitle: g.title, childName },
        seenKey,
        female,
      });
    }

    // ── First payout ──────────────────────────────────────────
    const hasPayout = transactions.some((t) => t.type === "payout");
    if (hasPayout && !seenMoments["first-payout:1"]) {
      const firstPayout = transactions.find((t) => t.type === "payout");
      candidates.push({
        context: "first-payout",
        data: {
          rewardMinor: firstPayout?.amountMinor
            ? Math.abs(firstPayout.amountMinor)
            : undefined,
          childName,
        },
        seenKey: "first-payout:1",
        female,
      });
    }

    // ── Shield used (per week) ────────────────────────────────
    if (streakStatus.shieldJustConsumed) {
      const today = new Date();
      const weekStart = new Date(today);
      const dow = today.getDay() || 7; // Mon=1
      weekStart.setDate(today.getDate() - (dow - 1));
      weekStart.setHours(0, 0, 0, 0);
      const seenKey = `shield-used:${dayKey(weekStart)}`;
      if (!seenMoments[seenKey]) {
        candidates.push({
          context: "shield-used",
          data: { streakDays, childName },
          seenKey,
          female,
        });
      }
    }

    // ── Welcome (pierwszy dzień konta) ────────────────────────
    if (firebaseUser?.metadata.creationTime) {
      const created = new Date(firebaseUser.metadata.creationTime);
      const today = new Date();
      if (isSameDay(created, today) && !seenMoments["welcome:1"]) {
        candidates.push({
          context: "welcome",
          data: { childName },
          seenKey: "welcome:1",
          female,
        });
      }
    }

    // ── Comeback (>5 dni od ostatniej aktywności) ─────────────
    if (ownApproved.length >= 1) {
      const sorted = [...ownApproved].sort(
        (a, b) => b.completedAt.toDate().getTime() - a.completedAt.toDate().getTime(),
      );
      const lastActivity = sorted[0].completedAt.toDate();
      const daysSinceLast = Math.floor(
        (Date.now() - lastActivity.getTime()) / 86400000,
      );
      if (daysSinceLast >= 5) {
        const seenKey = `comeback:${dayKey(new Date())}`;
        if (!seenMoments[seenKey]) {
          candidates.push({
            context: "comeback",
            data: { childName },
            seenKey,
            female,
          });
        }
      }
    }

    // ── Complete day (wszystkie dzienne zadania approved dziś) ─
    {
      const today = new Date();
      const todayKey = dayKey(today);
      const seenKey = `complete-day:${todayKey}`;
      if (!seenMoments[seenKey] && uid) {
        const myDaily = chores.filter(
          (c) =>
            c.active &&
            c.assignedTo === uid &&
            c.schedule.type === "daily",
        );
        if (myDaily.length > 0) {
          const todayApproved = ownApproved.filter((c) =>
            isSameDay(c.completedAt.toDate(), today),
          );
          const doneIds = new Set(todayApproved.map((c) => c.choreId));
          const allDone = myDaily.every((c) => doneIds.has(c.id));
          if (allDone) {
            candidates.push({
              context: "complete-day",
              data: { childName },
              seenKey,
              female,
            });
          }
        }
      }
    }

    // Tylko dorzucaj nowe candidaty których jeszcze nie ma w queue
    // ani nie zostały świeżo zdismisowane (race z server-write).
    setQueue((prev) => {
      const existing = new Set(prev.map((m) => m.seenKey));
      const fresh = candidates.filter(
        (c) =>
          !existing.has(c.seenKey) && !dismissedRef.current.has(c.seenKey),
      );
      if (fresh.length === 0) return prev;
      return [...prev, ...fresh];
    });
  }, [
    isChild,
    uid,
    ownApproved,
    transactions,
    goals,
    chores,
    proposals,
    shieldUseCount,
    streakStatus.shieldJustConsumed,
    firebaseUser?.metadata.creationTime,
    seenMoments,
    female,
    childName,
  ]);

  const dismiss = () => {
    setQueue((prev) => {
      const [head, ...rest] = prev;
      if (head && uid) {
        // Lokalny tombstone PRZED setQueue commit — gdy effecty re-firują
        // po zmianie state queue, widzą key w refie i nie dorzucają go z
        // powrotem zanim server-write markMomentSeen zdąży się rozpropagować.
        dismissedRef.current.add(head.seenKey);
        void markMomentSeen(uid, head.seenKey).catch(() => {
          // Server-write nie poszedł — pozwól retry przy następnym deps change
          dismissedRef.current.delete(head.seenKey);
        });
      }
      return rest;
    });
  };

  return { active: queue[0] ?? null, dismiss };
}
